import React, { useState, useEffect } from 'react';

import { withStyles } from '@material-ui/core/styles';
import isEqual from 'react-fast-compare';
import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import DefaultDialog from 'ui/dialog/DefaultDialog';
import ConfirmationDialog from 'ui/dialog/ConfirmationDialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import Typography from '@material-ui/core/Typography';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Toolbar from '@material-ui/core/Toolbar';

import VaultInfo from './forms/VaultInfo';

import EditableLabel from '../../../ui/EditableLabel';

import AddRecipients from './forms/AddRecipients';
import { RecipientsPermission, VaultPermission } from './constants';
import RecipientsList from './forms/RecipientsList';
import { DELETE_VAULT_WITHOUT_RECIPIENTS } from './constants';

const styles = theme => ({
    appBar: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.secondary.main,
        paddingBottom: '10px',
        position: 'relative',
        '&:after': {
            content: '""',
            borderBottom: `3px solid ${theme.palette.grey['300']}`,
            position: 'absolute',
            bottom: 18,
            left: -24,
            right: -24,
            zIndex: -1,
        },
    },
    appBarTab: {
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    dialog: {
        maxWidth: 760,
        height: 'auto',
        minHeight: 0,
    },
    dialogToolbar: { padding: 0 },
    dialogToolbarTabs: { flex: 1 },
    actions: {
        padding: theme.spacing(2),
        justifyContent: 'space-between',
    },
    actionButton: {
        marginRight: theme.spacing(2),

        '&:last-child': {
            marginRight: 0,
        },
    },
});

const MAX_VAULT_NAME_LENGTH = 150;

const vaultAlreadyExist = (vaults, vaultInfo) => {
    return Boolean(
        vaults.find(
            vault =>
                vault.nickname === vaultInfo.nickname &&
                vault.uuid !== vaultInfo.uuid
        )
    );
};

const getInitialVaultState = currentVault => {
    if (!currentVault) return {};
    return {
        uuid: currentVault.uuid,
        nickname: currentVault.nickname,
        owner: currentVault.owners,
        recipient_count: currentVault.recipient_count,
        available_sub_companies: currentVault.available_sub_companies,
        subcompanies: [],
    };
};

const renderVaultDialogTitle = (
    vaultInfo,
    vaultInfoErrors,
    onChange,
    editable
) => {
    if (editable) {
        return (
            <EditableLabel
                fieldInfo={vaultInfo}
                fieldInfoErrors={vaultInfoErrors}
                fieldName="nickname"
                onChange={(prop, val) => onChange(val)}
            />
        );
    }
    return <Typography variant="h5">{vaultInfo.nickname}</Typography>;
};

const validate = (vaultInfo, vaults, recipientsInfo, newRecipientsInfo) => {
    const trimmedNickname = (vaultInfo.nickname || '').trim();

    const errors = {
        nickname: trimmedNickname ? '' : 'Required',
        owner: '',
    };

    if (!errors.nickname) {
        if (trimmedNickname.length > MAX_VAULT_NAME_LENGTH) {
            errors.nickname = `Invalid: value must not exceed ${MAX_VAULT_NAME_LENGTH} characters`;
        } else if (vaultAlreadyExist(vaults, vaultInfo)) {
            errors.nickname = 'Please enter a unique vault name.';
        }
    }

    const totalRecipients = recipientsInfo.length + newRecipientsInfo.length;
    if (totalRecipients > 0) {
        const activeOwners = countActiveOwners(
            recipientsInfo,
            newRecipientsInfo
        );
        if (activeOwners === 0) {
            errors.owner = 'At least one recipient must have Owner permissions';
        }
    }

    const valid = Object.values(errors).reduce((acc, val) => {
        if (acc) {
            return !Boolean(val);
        }
        return acc;
    }, true);

    return [valid, errors];
};

const countActiveOwners = (existingRecipients, newRecipients) => {
    const existingOwners = existingRecipients.filter(
        item =>
            item.permissions === VaultPermission.Owner &&
            !item.is_pending_delete
    ).length;

    const newOwners = newRecipients.filter(
        item =>
            item.permissions === VaultPermission.Owner &&
            !item.is_pending_delete
    ).length;

    return existingOwners + newOwners;
};

function VaultForm(props) {
    const {
        classes,
        open,
        onOk,
        onCancel,
        cancelButtonName = 'Cancel',
        okButtonName = 'Save',
        onDelete,
        currentVault,
        vaults,
        editable,
    } = props;

    const [vaultChanged, setVaultChanged] = useState(false);
    const [tab, setTab] = useState(0);
    const [vaultInfo, setVaultInfo] = useState(
        getInitialVaultState(currentVault)
    );
    const [recipientsInfo, setRecipientsInfo] = useState(
        currentVault?.recipients.map(r => ({ ...r }))
    );

    const [newRecipientsInfo, setNewRecipientsInfo] = useState([]);

    const [vaultInfoErrors, setVaultInfoErrors] = useState({
        nickname: '',
        owner: '',
    });

    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    useEffect(() => {
        if (
            !isEqual(vaultInfo?.nickname, currentVault?.nickname) ||
            !isEqual(recipientsInfo, currentVault?.recipients) ||
            newRecipientsInfo.length > 0
        ) {
            setVaultChanged(true);
        } else {
            setVaultChanged(false);
        }
    }, [vaultInfo.nickname, recipientsInfo, newRecipientsInfo]);

    const getAccounts = recipients => {
        return recipients.reduce((acc, item) => {
            if (new Set(['account', 'invitation', 'non-org']).has(item.type)) {
                let res = {
                    account: item.name,
                    permissions: item.permissions,
                    uuid: item.uuid,
                };
                acc.push(res);
            }
            return acc;
        }, []);
    };

    const getGroups = recipients => {
        return recipients.reduce((acc, item) => {
            if (item.type === 'group') {
                acc.push({
                    group: item.name,
                    permissions: item.permissions,
                    uuid: item.uuid,
                });
            }
            return acc;
        }, []);
    };

    const createPayload = vaultInfo => {
        const allRecipients = [...newRecipientsInfo, ...recipientsInfo];
        const accounts = getAccounts(allRecipients);

        const groups = getGroups(allRecipients);

        const results = {
            uuid: vaultInfo.uuid,
            receiver: {
                accounts,
                groups,
            },
        };

        if (vaultInfo.nickname !== currentVault.nickname) {
            results.nickname = vaultInfo.nickname;
        }
        return results;
    };

    const handleSave = callback => () => {
        const totalRecipients =
            recipientsInfo.length + newRecipientsInfo.length;

        if (!totalRecipients) {
            setShowDeleteConfirmation(true);
            return;
        }

        const [valid, errors] = validate(
            vaultInfo,
            vaults,
            recipientsInfo,
            newRecipientsInfo
        );
        setVaultInfoErrors(errors);
        if (!valid) return;
        const results = createPayload(vaultInfo);
        callback(results);
    };

    // Use when the user removes all recipients from the vault.
    const handleConfirmDelete = () => {
        setShowDeleteConfirmation(false);
        onDelete(DELETE_VAULT_WITHOUT_RECIPIENTS);
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirmation(false);
    };

    // Generic helper to create permission change handlers
    const createPermissionChanger = (recipients, setRecipients) => (
        name,
        permissions
    ) => {
        setRecipients(
            recipients.map(item =>
                item.name === name ? { ...item, permissions } : item
            )
        );
    };

    // Generic helper to create recipient removal handlers
    const createRecipientRemover = (
        recipients,
        setRecipients
    ) => nameToRemove => {
        setRecipients(recipients.filter(({ name }) => name !== nameToRemove));
    };

    const changePermissionsForExistingRecipients = createPermissionChanger(
        recipientsInfo,
        setRecipientsInfo
    );

    const removeExistingRecipients = createRecipientRemover(
        recipientsInfo,
        setRecipientsInfo
    );

    const changePermissionsForNewRecipients = createPermissionChanger(
        newRecipientsInfo,
        setNewRecipientsInfo
    );

    const removeNewRecipients = createRecipientRemover(
        newRecipientsInfo,
        setNewRecipientsInfo
    );

    const changeVaultName = nickname => {
        setVaultInfo({ ...vaultInfo, nickname });
    };

    const addNewRecipient = data => {
        setNewRecipientsInfo([...newRecipientsInfo, data]);
    };

    return (
        <>
            <DefaultDialog
                open={open}
                onClose={onCancel}
                dialogClass={classes.dialog}
            >
                <DialogTitle>
                    {renderVaultDialogTitle(
                        vaultInfo,
                        vaultInfoErrors,
                        changeVaultName,
                        editable
                    )}
                </DialogTitle>
                <DialogContent style={{ paddingBottom: 5 }}>
                    <AppBar
                        position="static"
                        className={classes.appBar}
                        elevation={0}
                    >
                        <Toolbar className={classes.dialogToolbar}>
                            <Tabs
                                value={tab}
                                onChange={(event, val) => setTab(val)}
                                textColor="secondary"
                                className={classes.dialogToolbarTabs}
                            >
                                <Tab
                                    classes={{ root: classes.appBarTab }}
                                    label="Vault Info"
                                />
                                <Tab
                                    classes={{ root: classes.appBarTab }}
                                    label="Recipients"
                                />
                                {editable && (
                                    <Tab
                                        classes={{ root: classes.appBarTab }}
                                        label="Add Recipients"
                                    />
                                )}
                            </Tabs>
                        </Toolbar>
                    </AppBar>
                    {tab === 0 && <VaultInfo currentVault={currentVault} />}
                    {tab === 1 && (
                        <RecipientsList
                            editable={editable}
                            recipients={recipientsInfo}
                            permissionsOptions={RecipientsPermission}
                            onChange={changePermissionsForExistingRecipients}
                            onRemove={removeExistingRecipients}
                        />
                    )}
                    {tab === 2 && (
                        <AddRecipients
                            editable={editable}
                            newRecipientsInfo={newRecipientsInfo}
                            currentVault={currentVault}
                            onAddRecipient={addNewRecipient}
                            onChangePermission={
                                changePermissionsForNewRecipients
                            }
                            onRemoveRecipient={removeNewRecipients}
                        />
                    )}
                </DialogContent>
                {vaultInfoErrors.owner && (
                    <Typography
                        variant="body2"
                        style={{ color: 'red', padding: '0 24px 16px 24px' }}
                    >
                        {vaultInfoErrors.owner}
                    </Typography>
                )}
                {editable ? (
                    <DialogActions className={classes.actions}>
                        <div>
                            {tab === 0 && (
                                <Button
                                    onClick={() => onDelete()}
                                    color="primary"
                                >
                                    Delete
                                </Button>
                            )}
                        </div>
                        <div>
                            {onCancel && (
                                <Button
                                    onClick={onCancel}
                                    className={classes.actionButton}
                                    color="primary"
                                >
                                    {cancelButtonName}
                                </Button>
                            )}
                            <Button
                                disabled={!vaultChanged}
                                onClick={handleSave(onOk)}
                                color="secondary"
                                variant="contained"
                                className={classes.actionButton}
                                autoFocus
                            >
                                {okButtonName}
                            </Button>
                        </div>
                    </DialogActions>
                ) : (
                    <DialogActions withLeft>
                        <Button
                            onClick={onCancel}
                            variant="contained"
                            className={classes.actionButton}
                            color="secondary"
                        >
                            OK
                        </Button>
                    </DialogActions>
                )}
            </DefaultDialog>
            {showDeleteConfirmation && (
                <ConfirmationDialog
                    open={true}
                    title="Delete Vault"
                    subtitle1="Removing all recipients will permanently delete this Vault and all its data."
                    subtitle2="This action cannot be undone."
                    onCancel={handleCancelDelete}
                    onOk={handleConfirmDelete}
                    okButtonName="Delete Vault"
                    cancelButtonName="Cancel"
                />
            )}
        </>
    );
}

export default withStyles(styles)(VaultForm);
